import React from 'react';
import { Button, Tooltip } from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useExports } from '../../context/ExportsContext';

/**
 * Toolbar button that queues a CSV export for the current list. The
 * exports tray (mounted globally in App.jsx) handles the polling +
 * download UX.
 *
 * Props
 *   type     — 'orders' | 'customers' | 'inventory' | 'revenue'
 *   filters  — current filter snapshot to send to the backend
 *   disabled — optional disable flag (e.g. while initial list loads)
 *   variant  — MUI button variant (default 'outlined')
 */
const ExportButton = ({ type, filters, disabled, variant = 'outlined', size = 'small' }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { queueExport, jobs } = useExports();

    // Soft rate-guard: don't let the user queue a second export of
    // the same type while a previous one is still pending.
    const isQueueing = jobs.some(
        (j) => j.type === type && j.status === 'pending'
    );

    const handleClick = async () => {
        try {
            await queueExport(type, filters || {});
        } catch {
            // toast handled by context
        }
    };

    const label = isArabic ? 'تصدير CSV' : 'Export CSV';
    const tooltip = isQueueing
        ? (isArabic ? 'يوجد تصدير قيد التنفيذ بالفعل' : 'An export is already running')
        : '';

    return (
        <Tooltip title={tooltip} placement="top" arrow>
            <span>
                <Button
                    variant={variant}
                    size={size}
                    color="inherit"
                    startIcon={<FileDownload />}
                    onClick={handleClick}
                    disabled={disabled || isQueueing}
                >
                    {label}
                </Button>
            </span>
        </Tooltip>
    );
};

export default ExportButton;
